/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import React from "react";
import { ButtonBase, styled } from "@mui/material";
import { useAppRollenContext } from "../../../../Basis/Components/AppRollenContext";
import LANFooter from "../../../../Basis/Components/LANFooter";
import { nameof } from "../../../../Basis/Utils/AnyUtil";
import arrowBg from "./arrow-bg.png";
import { KachelColor } from "./KachelInfo";
import StartseiteViewModel from "./StartseiteViewModel";
import variablesScss from "../../../../Basis/Themes/variables.module.scss";

const StartseiteContainer = styled("div")({
    background: `url(${arrowBg})`,
    backgroundRepeat: "no-repeat",
    backgroundPositionX: "90%",
    width: "100%",
    height: "100%",
    overflow: "auto",
});

const StartseiteContent = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "column",
    flexWrap: "wrap",
    justifyContent: "center",
    alignItems: "center",
    width: "100%",
    gap: theme.spacing(),
    minHeight: "calc(100% - 20px)",
    paddingBlock: theme.spacing(),
}));

const StartseiteBlock = styled("div")(({ theme }) => ({
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    padding: theme.spacing(),
    gap: theme.spacing(),
    width: `min(34.5rem, calc(100% - ${theme.spacing(2)}))`,
    border: `2px solid ${theme.palette.divider}`,
    borderRadius: "0.5rem",
    backgroundColor: "white",
}));

type KachelColorProp = { kachelColor: KachelColor };

const StartseiteKachel = styled(ButtonBase, {
    shouldForwardProp: prop => prop !== nameof<KachelColorProp>("kachelColor")
})<KachelColorProp>(({ theme, kachelColor }) => {
    const colors = (() => {
        switch (kachelColor) {
            case KachelColor.primary:
                return { main: variablesScss.colorsTertiaryMain, dark: variablesScss.colorsTertiaryDark };
            case KachelColor.secondary:
                return { main: variablesScss.colorsPrimaryMain, dark: variablesScss.colorsPrimaryDark };
            default:
                return { main: variablesScss.colorsGrayMain, dark: variablesScss.colorsGrayDark };
        }
    })();
    return {
        display: "flex",
        flexDirection: "row",
        flexWrap: "wrap",
        alignItems: "flex-start",
        paddingBlock: theme.spacing(),
        textAlign: "center",
        height: "6.2rem",
        width: "6.2rem",
        border: "2px solid",
        borderRadius: theme.spacing(),
        cursor: "pointer",
        backgroundColor: colors.main,
        borderColor: colors.main,
        color: theme.palette.getContrastText(colors.main),
        "&:hover": {
            backgroundColor: colors.dark,
            color: theme.palette.getContrastText(colors.dark),
            borderColor: colors.dark,
        }
    };
});

const IconContainer = styled("div")({
    flex: "100% 0 0",
    "& > .MuiSvgIcon-root": {
        fontSize: "200%",
        overflow: "visible",
    }
});

const CaptionContainer = styled("div")({
    flex: "100% 0 0",
    width: "100%",
    fontSize: "13px",
});

export default function StartseiteView() {
    const vm = new StartseiteViewModel();
    const appRollenContext = useAppRollenContext();
    return (
        <StartseiteContainer>
            <StartseiteContent>
                {appRollenContext.istNutzend && vm.bloecke.map((kacheln, index) => (
                    <StartseiteBlock key={"block" + index}>
                        {kacheln.map(kachel => (
                            <StartseiteKachel
                                key={kachel.kachelIdentifier}
                                kachelColor={kachel.color}
                                onClick={kachel.onClick}
                            >
                                <IconContainer>
                                    {kachel.icon}
                                </IconContainer>
                                <CaptionContainer>
                                    {kachel.caption}
                                </CaptionContainer>
                            </StartseiteKachel>
                        ))}
                    </StartseiteBlock>))}
            </StartseiteContent>
            <LANFooter />
        </StartseiteContainer>
    );
}